import { InjectionKey, inject, provide, ref, shallowReadonly } from 'vue';
import { createPeriod, type PeriodKey } from '@/ts/composables/usePeriod';
import { createCalculator, type CalculatorKey } from '@/ts/composables/useCalculator';
import { createShowSymbols } from '@/ts/composables/useShowSymbols';
import { createHoverBox } from '@/ts/composables/useHoverBox';
import { createMarketCarpetData } from '@/ts/composables/useMarketCarpetData';
import { createRange } from '@/ts/composables/useRange';
import { createSizeBy, type SizeByKey } from '@/ts/composables/useSizeBy';
import { createColorScheme, type ColorSchemeKey } from '@/ts/composables/useColorScheme';
import { createDataTime } from '@/ts/composables/useDataTime';
import { createSideTable } from '@/ts/composables/useSideTable';
import { createFullscreen } from '@/ts/composables/useFullscreenMode';
import { createUrlSync } from '@/ts/composables/useUrlSync';

export interface MarketCarpetContextOptions {
  scan?: string;
  period?: PeriodKey;
  calculator?: CalculatorKey;
  colorScheme?: ColorSchemeKey;
  sizeBy?: SizeByKey;
  showSymbols?: boolean;
  autoRefresh?: boolean;
  showInfoHover?: boolean;
  showMiniChart?: boolean;
  showSideTable?: boolean;
  syncUrl?: boolean;
}

// Wires all composable factories together with the correct dependency order
export function createMarketCarpetContext(options: MarketCarpetContextOptions = {}) {
  const scan = ref(options.scan ?? '');
  const { periodKey } = createPeriod({ period: options.period });
  const { calculatorKey, calculator } = createCalculator({ calculator: options.calculator });
  const { showSymbols } = createShowSymbols({ showSymbols: options.showSymbols });
  const { hoverData, setLeafBoxHoverData, setParentBoxHoverData, clearHoverData, isHeaderShown, isSharpChartShown } = createHoverBox({
    showInfoHover: options.showInfoHover,
    showMiniChart: options.showMiniChart
  });

  const {
    rawData, dataState, mapRoot, currentNode, leafNodes, currentNodeLeafNodes,
    dates, lastUpdated, period, isAutoRefreshEnabled, isZoomed, zoomedInName,
    fetchData, zoomIn, zoomOut
  } = createMarketCarpetData(
    { calculator, periodKey },
    { autoRefresh: options.autoRefresh }
  );

  const { range, numericSteps } = createRange({ period, calculatorKey });
  const { sizeByKey, sizeBy } = createSizeBy({ leafNodes }, { sizeBy: options.sizeBy });
  const { colorSchemeKey, colorScheme, colorToNumberSpan, getParentColor, getLeafColor } = createColorScheme(
    { numericSteps, calculator, period },
    { colorScheme: options.colorScheme }
  );
  const { dateRange, formattedTimestamp } = createDataTime({ period, dates, lastUpdated });

  const { showSideTable, toggleSideTable, setShowSideTable } = createSideTable({ showSideTable: options.showSideTable });

  const fullscreenTarget = ref<HTMLElement | null>(null);
  const { isFullScreen, isFullScreenSupported, toggleFullScreen } = createFullscreen({ fullscreenTarget });

  createUrlSync(
    { periodKey, calculatorKey, colorSchemeKey, sizeByKey, showSymbols, isAutoRefreshEnabled, isHeaderShown, isSharpChartShown, showSideTable },
    { enabled: options.syncUrl ?? false }
  );

  return {
    rawData, dataState, mapRoot, currentNode, leafNodes, currentNodeLeafNodes, dates, lastUpdated,
    scan, periodKey, calculatorKey, colorSchemeKey, sizeByKey, showSymbols, isAutoRefreshEnabled,
    hoverData, setLeafBoxHoverData, setParentBoxHoverData, clearHoverData, isHeaderShown, isSharpChartShown,
    zoomedInName, isZoomed,
    showSideTable: shallowReadonly(showSideTable), toggleSideTable, setShowSideTable,
    fullscreenTarget, isFullScreen, isFullScreenSupported, toggleFullScreen,
    calculator, period, colorScheme, sizeBy, range, numericSteps, colorToNumberSpan,
    dateRange, formattedTimestamp,
    fetchData, zoomIn, zoomOut, getParentColor, getLeafColor,
  };
}

export type MarketCarpetContext = ReturnType<typeof createMarketCarpetContext>;

const MarketCarpetContextKey: InjectionKey<MarketCarpetContext> = Symbol('MarketCarpetContext');

export function provideMarketCarpetContext(marketCarpetContext: MarketCarpetContext) {
  provide(MarketCarpetContextKey, marketCarpetContext);
}

export function useMarketCarpetContext(): MarketCarpetContext {
  const marketCarpetContext = inject(MarketCarpetContextKey);

  if (!marketCarpetContext) {
    throw new Error('MarketCarpetContext not provided. Ensure component is wrapped in a MarketCarpet provider.');
  }

  return marketCarpetContext;
}
